'use client';

import { useEffect, useState, type MouseEvent, type TouchEvent } from 'react';
import { Point, type Canvas as FabricCanvas, type FabricObject } from 'fabric';

import type { DrawingJSON } from '@/types/drawing';
import { getItemTypes, type ItemType } from '@/config/itemTypes';

// The inbound postMessage payload from the embedding parent frame. All optional — the handler
// defends against a partial/absent message.
interface InboundMessage {
  drawing_JSON?: string;
  drawingType?: string;
  showPrompt?: boolean;
}
import { useToolContext } from '@/context/ToolContext';
import { dugoutFormations } from '@/config/dugoutFormations';
import exportData from '@/utils/exportData';
import importJSON from '@/utils/importJSON';
import placeFormation, { pickDugoutEmptyField } from '@/utils/placeFormation';
import readDugoutParams, {
  type DugoutInitParams,
} from '@/utils/readDugoutParams';
import deriveDugoutFieldVariation from '@/utils/deriveDugoutField';
import dugoutFieldZoom from '@/utils/dugoutFieldZoom';
import setCanvasBackground from '@/utils/setCanvasBackground';

import Aside from '@/components/Aside/Aside';
import CancelOverlay from '@/components/CancelOverlay/CancelOverlay';
import Canvas from '@/components/Canvas/Canvas';
import DefaultItems from '@/components/DefaultItems/DefaultItems';
import Editor from '@/components/Editor/Editor';
import Fields from '@/components/Fields/Fields';
import Header from '@/components/Header/Header';
import ItemMenu from '@/components/ItemMenu/ItemMenu';
import Items from '@/components/Items/Items';
import Lines from '@/components/Lines/Lines';
import Main from '@/components/Main/Main';
import QuickMenu from '@/components/QuickMenu/QuickMenu';
import Tools from '@/components/Tools/Tools';

const Home = () => {
  const ToolContextState = useToolContext();
  const { theme, isDugoutTheme } = ToolContextState;
  const catalogs = getItemTypes(theme);
  const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
  const canvasRef = ToolContextState.canvasRef;
  const [asideOverflow, setAsideOverflow] = useState('');
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const url =
      window.location === window.parent.location
        ? document.location.href
        : document.referrer;

    if (typeof window !== 'undefined') {
      window.addEventListener('message', listenForImport, { capture: false });
      // Dugout announces readiness as {action:'initialized'} to parent_url (the host's outbound
      // target, read from the URL) and builds its initial drawing (a formation or an empty pitch)
      // from the URL params; every other theme keeps the plain {type:'confirm'} handshake and waits
      // for the parent to post a drawing_JSON.
      if (isDugoutTheme) {
        const params = readDugoutParams();
        if (params.parentUrl) ToolContextState.setParentUrl(params.parentUrl);
        // A host may start a fresh drawing in symbols via ?iconType=alternateIcon (the tool has no
        // in-tool toggle). Set here in the mount effect — NOT in the useState initializer — so the
        // server and first client render agree (default), avoiding a hydration mismatch; the state
        // update then flips the menu/placement to symbols. An imported drawing_JSON still overrides.
        if (
          params.iconType === 'alternateIcon' ||
          params.iconType === 'defaultIcon'
        )
          ToolContextState.setIconType(params.iconType);
        window.parent.postMessage(
          { action: 'initialized' },
          params.parentUrl ?? url,
        );
        initDugoutDrawing(params);
      } else {
        window.parent.postMessage({ type: 'confirm' }, url);
      }
    }
    if (navigator)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time iOS detection on mount
      setIsIOS(
        /iPad|iPhone|iPod/.test(navigator?.platform) ||
          (navigator?.platform === 'MacIntel' && navigator?.maxTouchPoints > 1),
      );

    return () => {
      if (typeof window !== 'undefined')
        window.removeEventListener('message', listenForImport, false);
    };
  }, []);

  useEffect(() => {
    loadItemImages();
    ToolContextState.canvasStateRef.current?.requestRenderAll();
  }, [ToolContextState.canvasStateRef]);

  useEffect(() => {
    // The {type:'recieved'} ack is rinus-only. Dugout sets parentUrl from the URL param (so this
    // effect still fires) but its contract has no ack step — it goes straight from
    // {action:'initialized'} to the host posting drawing_JSON — so stay silent for dugout.
    if (ToolContextState.parentUrl && !isDugoutTheme)
      window.parent.postMessage(
        { type: 'recieved' },
        ToolContextState.parentUrl,
      );
  }, [ToolContextState.parentUrl]);

  useEffect(() => {
    ToolContextState.canvasStateRef.current.fieldSelected =
      ToolContextState?.fieldSelected || ToolContextState?.fieldTypes[0];
    // Dugout fields carry no `dimensions` (the pitch is a `pitchType`, not a size), so guard on it
    // rather than the field: for every rinus field the two are equivalent (all have dimensions), so
    // this stays byte-identical there while leaving dugout's dimensions as the initial [].
    if (ToolContextState.fieldSelected?.dimensions)
      ToolContextState.setFieldDimensions(
        ToolContextState.fieldSelected.dimensions,
      );
    void setField();
  }, [ToolContextState?.fieldSelected]);

  useEffect(() => {
    if (ToolContextState.reloadField) void setField();
    ToolContextState.setReloadField(false);
  }, [ToolContextState.reloadField]);

  useEffect(() => {
    const orientation = window.matchMedia('(orientation: portrait)');

    const checkIsMobile = () => {
      ToolContextState.setIsMobile(window.innerWidth < 930);
      if (window.innerWidth < 930) {
        ToolContextState.canvasStateRef.current.setDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
        ToolContextState.canvasStateRef.current.requestRenderAll();
      }
    };
    const orientationChange = () => {
      void setField();
    };
    checkIsMobile();

    // eslint-disable-next-line unicorn/prefer-observer-apis -- a window 'resize' listener reading window.innerWidth; ResizeObserver observes element box size, not the viewport, so it is not equivalent
    window.addEventListener('resize', checkIsMobile);
    if (isIOS) {
      window.addEventListener('orientationchange', orientationChange);
    } else {
      orientation.addEventListener('change', orientationChange);
    }

    return () => {
      window.removeEventListener('resize', checkIsMobile);
      if (isIOS) {
        window.removeEventListener('orientationchange', orientationChange);
      } else {
        orientation.removeEventListener('change', orientationChange);
      }
    };
  }, []);

  const loadItemImages = () => {
    ToolContextState.setShirtTypes(catalogs.shirtTypes);
    ToolContextState.setGoalTypes(catalogs.goalTypes);
    ToolContextState.setItemTypes(catalogs.itemTypes);
    ToolContextState.setConeTypes(catalogs.coneTypes);
    ToolContextState.setFieldTypes(catalogs.fieldTypes);
    ToolContextState.setToolTypes(catalogs.toolTypes);
    ToolContextState.setLineTypes(catalogs.lineTypes);
    ToolContextState.setDefaultTypes(catalogs.defaultTypes);
  };

  // Dugout new-drawing init (mount, dugout-only). A formation (`formatie*` params) is placed on its
  // own pitch via placeFormation; otherwise an empty pitch is selected from pitchType/voetbalvorm. An
  // existing drawing still arrives later via postMessage (listenForImport) and clears/overrides this.
  const initDugoutDrawing = (params: DugoutInitParams) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const key =
      params.formatieEigenTeam && params.formatieTegenstander
        ? `${params.formatieEigenTeam}_${params.formatieTegenstander}`
        : null;
    const scene = key ? dugoutFormations[key] : undefined;

    if (scene) {
      ToolContextState.setFieldSelected(
        catalogs.fieldTypes.find((field) => field.variation === scene.field) ??
          null,
      );
      placeFormation(canvas, scene, catalogs, theme);
    } else {
      const emptyField = pickDugoutEmptyField(
        params.pitchType,
        params.voetbalvorm,
        catalogs.fieldTypes,
      );
      if (emptyField) ToolContextState.setFieldSelected(emptyField);
    }
  };

  const listenForImport = (event: MessageEvent) => {
    // The Dugout host (Moodle) posts the inbound drawing as a JSON *string* envelope (its iframe comm
    // library stringifies { drawing_JSON }), whereas rinus's frontend posts a structured object. Parse
    // the string form for dugout so importJSON receives the drawing; every other theme is untouched.
    let parsedData = event.data as InboundMessage | undefined;
    if (isDugoutTheme && typeof event.data === 'string') {
      try {
        parsedData = JSON.parse(event.data) as InboundMessage;
      } catch {
        return;
      }
    }

    // Enable prompt when clicked outside the iFrame
    if (parsedData?.showPrompt) {
      ToolContextState.setShowCancelOverlay(true);
      return;
    }
    if (parsedData) {
      const drawingJSON = parsedData?.drawing_JSON;
      const drawingType = parsedData?.drawingType;
      if (drawingJSON) {
        const drawingData = JSON.parse(drawingJSON) as DrawingJSON;
        // Restore the drawing's own shape mode: a dugout drawing made in symbols persists
        // iconType:'alternateIcon', and must reload in symbols so the menu (and any item added next)
        // match the canvas — otherwise the tool falls back to its defaultIcon default and desyncs. Gated
        // to dugout; other themes always serialize 'defaultIcon' (their only value), so they are untouched.
        if (isDugoutTheme && drawingData.iconType)
          ToolContextState.setIconType(drawingData.iconType);
        // Dugout pitches always FILL the 615 canvas at scale 1, and its objects are stored in absolute
        // canvas coords — so normalise the reference background to the origin, and recalculateObjectPositions
        // keeps the imported objects exactly where they are (the legacy `left:307.5` centre-origin
        // background would otherwise shift them). Other themes keep the real imported background props.
        ToolContextState.canvasStateRef.current.importedBackgroundProps =
          isDugoutTheme
            ? { left: 0, top: 0, scaleX: 1, scaleY: 1 }
            : drawingData.backgroundImage;
        // Dugout drawings carry no `fieldVariation` — derive the pitch from the backgroundImage PNG
        // filename. The SPREAD gives a fresh field reference so the [fieldSelected] effect always
        // re-runs setField (the empty pitch set on mount is the same object) — repositioning the field
        // to fill the canvas and replacing the mis-placed legacy background. Other themes: original logic.
        // Our own dugout exports carry an explicit `fieldVariation`, so prefer it: the serialized
        // `backgroundImage` is an inlined data URI (no filename to derive a pitch from), so deriving
        // from it would always collapse to field-01 and silently lose field-02/03/futsal on reload.
        // Genuine legacy Dugout drawings have no `fieldVariation`, so fall back to deriving the pitch
        // from the backgroundImage PNG path (Phase 6 back-compat), unchanged for them.
        const dugoutField = isDugoutTheme
          ? catalogs.fieldTypes.find(
              (field) =>
                field.variation ===
                (drawingData.fieldVariation ??
                  deriveDugoutFieldVariation(
                    drawingData.backgroundImage?.src as string | undefined,
                  )),
            )
          : undefined;
        const importedField = isDugoutTheme
          ? dugoutField
            ? { ...dugoutField }
            : null
          : drawingData?.fieldVariation
            ? catalogs.fieldTypes.find(
                (field) => field.variation === drawingData.fieldVariation,
              )
            : parsedData?.drawingType === 'indoor'
              ? { ...catalogs.fieldTypes[4] }
              : { ...catalogs.fieldTypes[0] };
        const afterImport = () => {
          ToolContextState.setFieldSelected(importedField ?? null);
        };
        importJSON(
          drawingJSON,
          ToolContextState.canvasStateRef.current,
          ToolContextState.isMobileStateRef.current,
          theme,
          afterImport,
        );

        if (drawingType) {
          if (!ToolContextState.drawingType)
            ToolContextState.setDrawingType(drawingType);
        } else {
          if (!ToolContextState.drawingType)
            ToolContextState.setDrawingType('outdoor');
        }
      } else if (!isDugoutTheme) {
        // Non-drawing_JSON fallback (a bare ready/confirm handshake) → pick a default rinus field.
        // Skipped for dugout: its field comes from the URL-param formation (initDugoutDrawing) or a
        // real imported drawing_JSON, so the standalone self-`confirm` must not clobber the pitch.
        if (drawingType) {
          if (!ToolContextState.drawingType)
            ToolContextState.setDrawingType(drawingType);
          if (!ToolContextState.fieldSelected) {
            ToolContextState.setFieldSelected(
              parsedData?.drawingType === 'indoor'
                ? { ...catalogs.fieldTypes[4] }
                : { ...catalogs.fieldTypes[0] },
            );
          }
        } else {
          if (!ToolContextState.drawingType)
            ToolContextState.setDrawingType('outdoor');
          if (!ToolContextState.fieldSelected)
            ToolContextState.setFieldSelected({ ...catalogs.fieldTypes[0] });
        }
      }

      // Set parent url one time only
      if (!ToolContextState.parentUrl && event.origin) {
        ToolContextState.setParentUrl(event.origin);
      }
    }
  };

  const onSubmit = () => {
    exportData(
      ToolContextState.canvasStateRef.current,
      true,
      ToolContextState.iconType,
      ToolContextState.fieldDimensions,
      ToolContextState.parentUrl,
      ToolContextState.fieldSelected?.variation,
      theme,
    );
  };

  const onExit = () => {
    // Dugout signals teardown with {action:'destroy'} (the host then closes the iframe); every other
    // theme keeps {type:'close'}.
    if (window.parent)
      window.parent.postMessage(
        isDugoutTheme ? { action: 'destroy' } : { type: 'close' },
        ToolContextState.parentUrl,
      );
  };

  const onEditorInputBlur = () => {
    void setField();
    if (window.innerWidth < 930) {
      ToolContextState.canvasStateRef.current.setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      ToolContextState.canvasStateRef.current.requestRenderAll();
    }
  };

  const asideDrag = (style: string) => {
    setAsideOverflow(style);
  };

  const setLineType = (
    e: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>,
  ) => {
    // Prevent user from pushing an element into the canvas when scrolling the ItemMenu.
    if (ToolContextState.preventClickBeforeScroll) return;

    ToolContextState.setIsDrawingLine(true);

    const lineSelected = (e.target as HTMLElement).dataset.variation!;
    if (
      lineSelected === ToolContextState.lineSelected &&
      !ToolContextState.isMobileStateRef.current
    ) {
      // de-select selected line type
      canvas.selection = true;
      ToolContextState.setStoreLineType('');
      canvas.defaultCursor = '';
      ToolContextState.setLineSelected(null);
    } else {
      // select target line type
      canvas.selection = false;
      ToolContextState.setStoreLineType(lineSelected);
      ToolContextState.setLineSelected(lineSelected);

      if (ToolContextState.isMobileStateRef.current) {
        ToolContextState.setIsMenuActive(false);
        ToolContextState.setSelection(lineSelected);
      }
    }
  };

  const setField = async () => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const field = canvas?.fieldSelected as ItemType | undefined;
    if (!field) return;

    canvas?.discardActiveObject();
    const canvasSize = 615;
    // Dugout's pitches are full-canvas 615×615 PNGs, so they fill the 615 canvas at scale 1 (centred
    // at 0,0 by the offset maths below). rinus fields are smaller SVGs shown at 1.2× with a green
    // surround. Gated so every other theme keeps the exact 1.2 factor (and the coordinate system that
    // item placement/formations depend on).
    const scaleFactor = isDugoutTheme ? 1 : 1.2;
    const leftOffset = (field.width! / 2) * scaleFactor;
    const topOffset = (field.height! / 2) * scaleFactor;
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    const newFieldLeft = centerX - leftOffset;
    const newFieldTop = centerY - topOffset;

    canvas.currentScale = scaleFactor;
    ToolContextState.setScale(scaleFactor);
    let allObjects = canvas.getObjects();

    const recalculateObjectPositions = (
      objects: FabricObject[],
      scaleFactor: number,
    ) => {
      for (const obj of objects) {
        if (obj.name!.includes('line')) continue;
        const getOriginValuesFromBackground = (
          backgroundProps: Record<string, unknown>,
          object: FabricObject,
        ) => {
          const backgroundScale = Number(backgroundProps.scaleX) || 1;
          const originFieldLeft =
            (Number(backgroundProps.left) / backgroundScale) * 1;
          const originObjectLeft = (object.left / backgroundScale) * 1;
          const newLeft =
            (originObjectLeft - originFieldLeft) * scaleFactor + newFieldLeft;
          const originLeft = (newLeft / scaleFactor) * 1;
          object.originLeft = Math.floor(originLeft);

          const originFieldTop =
            (Number(backgroundProps.top) / backgroundScale) * 1;
          const originObjectTop = (object.top / backgroundScale) * 1;
          const newTop =
            (originObjectTop - originFieldTop) * scaleFactor + newFieldTop;
          const originTop = (newTop / scaleFactor) * 1;
          object.originTop = Math.floor(originTop);
          if (
            ['arrowHead', 'pointCurve', 'pointStart', 'pointEnd'].includes(
              object.name!,
            )
          ) {
            object.originScaleX = 1;
            object.originScaleY = 1;
          } else {
            object.originScaleX = (object.scaleX / backgroundScale) * 1;
            object.originScaleY = (object.scaleY / backgroundScale) * 1;
          }
        };

        if (!obj.originLeft)
          getOriginValuesFromBackground(
            canvas.importedBackgroundProps ?? {},
            obj,
          );
        obj.set({
          scaleX: obj.originScaleX
            ? obj.originScaleX * scaleFactor
            : obj.scaleX,
          scaleY: obj.originScaleY
            ? obj.originScaleY * scaleFactor
            : obj.scaleY,
          left: (obj.originLeft ?? 0) * scaleFactor || obj.left,
          top: (obj.originTop ?? 0) * scaleFactor || obj.top,
        });

        const recalculatePathline = (pathline: FabricObject) => {
          if (!pathline?.originStrokeWidth) {
            pathline.originStrokeWidth = 3;
          }

          const path01 = pathline.pointStart!.left;
          const path02 = pathline.pointStart!.top;
          const path11 = pathline.pointCurve!.left;
          const path12 = pathline.pointCurve!.top;
          const path13 = pathline.pointEnd!.left;
          const path14 = pathline.pointEnd!.top;

          const path = [
            ['M', path01, path02],
            ['Q', path11, path12, path13, path14],
          ];

          pathline.set({
            left: 0,
            path,
            strokeWidth: Math.max(
              Math.round(pathline.originStrokeWidth * scaleFactor * 100) / 100,
              1.5,
            ),
          } as unknown as Partial<FabricObject>);
        };

        if (
          ['pointCurve', 'pointStart', 'pointEnd'].includes(obj.name!) &&
          obj.pathLine
        ) {
          recalculatePathline(obj.pathLine);
        }
      }
      canvas.requestRenderAll();
    };

    await setCanvasBackground(canvas, field.defaultIcon.src, {
      left: newFieldLeft,
      top: newFieldTop,
      scaleX: scaleFactor,
      scaleY: scaleFactor,
    });
    canvas.setDimensions({ width: canvasSize, height: canvasSize });
    allObjects = canvas.getObjects();
    recalculateObjectPositions(allObjects, scaleFactor);
    canvas.hasLoaded = true;

    // Dugout-only: zoom the viewport onto the pitch actually in use (RINUS-175). The 6v6 pitch sits in
    // the top half rather than centred, so this pans as well as scales: after setZoom(z) a scene point
    // s maps to viewport z*s - pan, so panning to (z*focus - half) puts `focus` in the viewport centre.
    // Applied unconditionally (zoom 1 focused on the canvas centre is the identity transform), so
    // switching 6v6 → 11v11 un-zooms. The reset button is enabled alongside so the user can always get
    // back to the whole canvas.
    if (isDugoutTheme) {
      const { zoom, focusX, focusY } = dugoutFieldZoom(field.variation);
      canvas.setZoom(zoom);
      canvas.absolutePan(
        new Point(
          zoom * focusX - canvas.getWidth() / 2,
          zoom * focusY - canvas.getHeight() / 2,
        ),
      );
      ToolContextState.setEnableResetButton(zoom !== 1);
    }

    ToolContextState.setReloadField(false);
  };

  return (
    <>
      <Header
        onCancel={() => {
          ToolContextState.setShowCancelOverlay(true);
        }}
        onSubmit={onSubmit}
      />
      <CancelOverlay
        isVisible={ToolContextState.showCancelOverlay}
        onCancel={() => {
          ToolContextState.setShowCancelOverlay(false);
        }}
        onSubmit={onSubmit}
        onExit={onExit}
      />
      <Main
        canvas={canvas}
        canvasEl={canvasRef?.current}
        iconType={ToolContextState.iconType}
        asideDrag={asideDrag}
      >
        {!ToolContextState.isMobileStateRef.current && (
          <Aside asideOverflow={asideOverflow}>
            <Items
              goalTypes={ToolContextState.goalTypes}
              itemTypes={ToolContextState.itemTypes}
              coneTypes={ToolContextState.coneTypes}
              shirtTypes={ToolContextState.shirtTypes}
              isDugoutTheme={isDugoutTheme}
              iconType={ToolContextState.iconType}
              drawingType={ToolContextState.drawingType}
            />
            <Lines
              setLineType={setLineType}
              lineTypes={ToolContextState.lineTypes}
              lineSelected={ToolContextState.lineSelected}
            />
            <Tools
              toolTypes={ToolContextState.toolTypes}
              iconType={ToolContextState.iconType}
            />
            {ToolContextState.fieldTypes.length > 0 && (
              <Fields
                setField={ToolContextState.setFieldSelected}
                fieldTypes={ToolContextState.fieldTypes}
                fieldSelected={ToolContextState.fieldSelected?.variation}
              />
            )}
          </Aside>
        )}
        <ItemMenu>
          <Items
            goalTypes={ToolContextState.goalTypes}
            itemTypes={ToolContextState.itemTypes}
            coneTypes={ToolContextState.coneTypes}
            shirtTypes={ToolContextState.shirtTypes}
            isDugoutTheme={isDugoutTheme}
            iconType={ToolContextState.iconType}
            isMobile={true}
          />
          <Lines
            setLineType={setLineType}
            lineTypes={ToolContextState.lineTypes}
            lineSelected={ToolContextState.lineSelected}
            isMobile={true}
          />
          <Tools
            toolTypes={ToolContextState.toolTypes}
            iconType={ToolContextState.iconType}
            isMobile={true}
          />
          <Fields
            setField={ToolContextState.setFieldSelected}
            fieldTypes={ToolContextState.fieldTypes}
            fieldSelected={ToolContextState.fieldSelected?.variation}
            isMobile={true}
          />
        </ItemMenu>
        <QuickMenu>
          <DefaultItems
            defaultTypes={ToolContextState.defaultTypes}
            isDugoutTheme={isDugoutTheme}
            iconType={ToolContextState.iconType}
          />
        </QuickMenu>
        <div className="canvas-editor-container">
          <Canvas iconType={ToolContextState.iconType}></Canvas>
          {ToolContextState.selection && (
            <Editor
              selection={ToolContextState.selection}
              onEditorInputBlur={onEditorInputBlur}
              iconType={ToolContextState.iconType}
            />
          )}
        </div>
      </Main>
    </>
  );
};

export default Home;
